
/* eslint-disable max-lines */
import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { LazyLoad, Box, ResponsiveBox } from '@usap/uicomponents'
import { LocalContext } from '../../helpers/useLocalContext'
import { useSerpContext } from '../../contexts/serp.context'
import getInventoryDetails from '../../helpers/getInventoryDetails'
import { GTM } from '../../helpers'
import { getSyncSEDetails } from '../../helpers/getShippingExpectationDate'
import { useLocationContext } from '../../contexts/location.context'
import useIsMounted from '../../../hooks/useIsMounted'
import styled from "styled-components";
import { Title } from '@usap/widgets'
import { Carousel } from '../Carousel/Carousel'
import RecommendationViewCardLoadable from '../Loadables/RecommendationViewCard.loadable'
import { ItemWrapper } from "./Recommendations/Recommendation.css";
import { RecommendationWidgetNameEnum, useGetProductListingName } from '../../hooks/useGetProductListingName'


const MainContainer = styled(ResponsiveBox)`
    padding: 0 1rem;
    max-width: 1440px;

    @media (min-width: 600px){
        padding: 0 1.25rem;
    }
    @media(min-width: 768px){
        padding: 0 1.75rem;
    }
`
const RecommendationTitle = styled(Title)`
    line-height: 1.875rem;
    font-weight: 500;
    font-size: 1.25rem;
    margin-bottom: 1rem;

    @media (min-width: 768px){
        line-height: 2.25rem;
        font-weight: 600;
        font-size: 1.5rem;
    }
`
const styleCSS = {
  pad: { horizontal: 'small', vertical: 'medium' },
  margin: { horizontal: 'auto' },
  fxstyle: {
    other: { minWidth: '300px', overflowY: 'auto' },
    large: {},
  },
  fontsize: { fontSize: '20px' },
}

const RecomWrapper = styled.div`
  background-color: ${({ bgColor }) => bgColor};
  padding-top: 1.5rem;

  @media (min-width: 768px){
    padding-top: 2rem;
  }
`

const RecentlyViewedCarousel = ({ label = "", widgetName, bgColor = "#fff" }) => {
  const { isBot } = useContext(LocalContext)
  const [records, setRecords] = useState([])
  const [shippingExp, setShippingExp] = useState({})
  const isMounted = useIsMounted()
  const locationState = useLocationContext()
  const zipCode = (locationState || {}).postalCode
  const prevZipRef = useRef(null)

  const serpState = useSerpContext()
  const { recentlyViewed, recentlyViewedBefore } = serpState || {}
  const { getGroundDeliveryTime, viewItemListGTM } = GTM
  const [firstImpression, setFirstImpression] = useState(true)

  const setRecentlyViewed = useCallback(
    currentRecentlyViewedBefore => {
      serpState.setRecentlyViewedBefore(currentRecentlyViewedBefore + 1)
    },
    [serpState]
  )

  const pids = useMemo(() => records.map(item => item.id).join('|'), [records])

  const currentItemListName = useGetProductListingName({
    widgetName: RecommendationWidgetNameEnum?.[widgetName],
  });

  useEffect(() => {
    if (!zipCode || !records.length) return
    if (prevZipRef.current === zipCode && shippingExp?.length) return
    prevZipRef.current = zipCode

    const seChunks = []
    for (let i = 0; i < records.length; i += 6) {
      seChunks.push(records.slice(i, i + 6))
    }
    Promise.all(seChunks.map(chunk => getSyncSEDetails(chunk, true, undefined, zipCode)))
      .then(results => {
        if (!isMounted()) return
        setShippingExp(Object.assign({}, ...results))
      })
      .catch(err => console.error('error re-fetching recently viewed SE on zip change', err?.message || err))
  }, [zipCode, records])

  useEffect(() => {
    if (firstImpression && records.length > 0) {
      const recentlyViewedBeforeItem = parseInt(recentlyViewedBefore, 10)

      const newRecentlyViewedBefore =
        (Number.isNaN(recentlyViewedBeforeItem)
          ? 0
          : recentlyViewedBeforeItem) || 0

      Promise.all([
        getInventoryDetails(pids, false),
        getSyncSEDetails(records),
      ])
        .then(([invDetails, seData]) => {
          if (!isMounted()) return

          setShippingExp(seData)
          const newRecords = records.map(record => ({
            ...record,
            inventory:
              records.length > 1 ? invDetails[record.id] : invDetails,
            groundDeliveryTime: getGroundDeliveryTime(seData, record.sku),
          }));
          setRecords(newRecords)
          viewItemListGTM({
            listName: currentItemListName,
            widgetName: RecommendationWidgetNameEnum?.[widgetName],
            products: newRecords,
          });
        })
        .catch(() => {
          if (!isMounted()) return

          viewItemListGTM({
            listName: currentItemListName,
            widgetName: RecommendationWidgetNameEnum?.[widgetName],
            products: records,
          });

        })
        .finally(() => {
          if (!isMounted()) return
          setRecentlyViewed(newRecentlyViewedBefore)
        })

      setFirstImpression(false)
    }
  }, [
    recentlyViewedBefore,
    records,
    firstImpression,
    isMounted,
    setRecentlyViewed,
    getGroundDeliveryTime,
    pids,
  ])

  const formatWidgetNameIds = (str) => {
    if (typeof str !== 'string') return ''
    return str.replace(/\s+/g, '_').toLowerCase();
  }

  useEffect(() => {
    if (__BROWSER__) {
      // Only update records if recentlyViewed is different from the current records
      if (recentlyViewed && recentlyViewed !== records) {
        setRecords(recentlyViewed || []);
      }
    }
  }, [recentlyViewed, records]);

  if (!(records && records.length)) return null

  return (
    <RecomWrapper bgColor={bgColor}>
      <LazyLoad once height={400} offset={100} isBot={isBot}>
        <MainContainer
          id={formatWidgetNameIds(widgetName)}
          pad={styleCSS.pad}
          margin={styleCSS.margin}
          data-recid={label}
        >
          {records.length !== 0 && (
            <>
              <Box id={`${formatWidgetNameIds(widgetName)}_Heading`}>
                <RecommendationTitle
                  type="h2"
                  label={label}
                />
              </Box>

              <Carousel id={`carousel-${widgetName}`}>
                {records &&
                  records.map((record, index) => (
                    <ItemWrapper aria-label="Item Wrapper" key={`item-${record.id}`}>
                      <RecommendationViewCardLoadable
                        data={record}
                        getInventoryDetails={getInventoryDetails}
                        key={record.id}
                        index={index}
                        shippingExp={shippingExp}
                        widgetName={widgetName || "RecentlyViewed"}
                        lastRecord={records.length === index + 1}
                      />
                    </ItemWrapper>
                  ))}
              </Carousel>
            </>
          )}
        </MainContainer>
      </LazyLoad>
    </RecomWrapper>
  )
}
export default RecentlyViewedCarousel